import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

interface ExperienceData {
  id?: string;
  company_name: string;
  position: string;
  start_date: string;
  end_date: string;
  description: string;
  achievements: string[];
  display_order: number;
}

interface ExperienceFormProps {
  initialData?: ExperienceData;
  onSuccess?: () => void;
}

export const ExperienceForm = ({ initialData, onSuccess }: ExperienceFormProps) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    company_name: "",
    position: "",
    start_date: "",
    end_date: "",
    description: "",
    achievements: "",
    display_order: 0,
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        company_name: initialData.company_name || "",
        position: initialData.position || "",
        start_date: initialData.start_date || "",
        end_date: initialData.end_date || "",
        description: initialData.description || "",
        achievements: initialData.achievements?.join("\n") || "",
        display_order: initialData.display_order || 0,
      });
    }
  }, [initialData]);

  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const achievementsArray = data.achievements
        .split("\n")
        .filter((a) => a.trim() !== "");

      if (initialData?.id) {
        // Update existing
        const { error } = await supabase
          .from("work_experience")
          .update({
            ...data,
            achievements: achievementsArray,
          })
          .eq("id", initialData.id);
        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase.from("work_experience").insert([
          {
            ...data,
            achievements: achievementsArray,
          },
        ]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-experiences"] });
      queryClient.invalidateQueries({ queryKey: ["experiences"] });
      toast.success(initialData ? "Experience updated successfully" : "Experience added successfully");

      if (!initialData) {
        setFormData({
          company_name: "",
          position: "",
          start_date: "",
          end_date: "",
          description: "",
          achievements: "",
          display_order: 0,
        });
      }

      onSuccess?.();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="company_name">Company Name *</Label>
          <Input
            id="company_name"
            value={formData.company_name}
            onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="position">Position *</Label>
          <Input
            id="position"
            value={formData.position}
            onChange={(e) => setFormData({ ...formData, position: e.target.value })}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="start_date">Start Date *</Label>
          <Input
            id="start_date"
            value={formData.start_date}
            onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="end_date">End Date (leave empty if current)</Label>
          <Input
            id="end_date"
            value={formData.end_date}
            onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={3}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="achievements">Achievements (one per line)</Label>
        <Textarea
          id="achievements"
          value={formData.achievements}
          onChange={(e) => setFormData({ ...formData, achievements: e.target.value })}
          rows={5}
          placeholder="Enter each achievement on a new line"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="display_order">Display Order</Label>
        <Input
          id="display_order"
          type="number"
          value={formData.display_order}
          onChange={(e) =>
            setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })
          }
        />
      </div>

      <Button type="submit" disabled={mutation.isPending} className="w-full">
        {mutation.isPending ? (initialData ? "Updating..." : "Adding...") : (initialData ? "Update Experience" : "Add Experience")}
      </Button>
    </form>
  );
};
